import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Moon, Sun, X, RefreshCw, Plus, Palette } from "lucide-react";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import ImageTracer from "imagetracerjs";
import ToolsNav from "../components/ToolsNav";
import QuickConvert from "../components/QuickConvert";

export default function ConvertJpgToSvg() {
  const [images, setImages] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preset, setPreset] = useState("default");
  const [colorCount, setColorCount] = useState([16]);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);
  const addMoreInputRef = useRef(null);

  const presets = {
    default: { name: "Default", desc: "Balanced quality" },
    posterized1: { name: "Posterized", desc: "Simplified colors" },
    curvy: { name: "Curvy", desc: "Smooth curves" },
    sharp: { name: "Sharp", desc: "Sharp edges" },
    detailed: { name: "Detailed", desc: "Maximum detail" },
    grayscale: { name: "Grayscale", desc: "Black and white" },
  };

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Convert JPG to SVG - KayTool";
  }, []);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files).filter(f => 
      f.type === 'image/jpeg' || f.name.toLowerCase().endsWith('.jpg') || f.name.toLowerCase().endsWith('.jpeg')
    );
    if (files.length > 0) processImages(files);
    else toast.error("Please drop JPG files only");
  };

  const traceImage = (imageUrl) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          
          let options = {};
          if (preset === 'posterized1') {
            options = { colorsampling: 0, numberofcolors: 4, mincolorratio: 0, colorquantcycles: 3 };
          } else if (preset === 'curvy') {
            options = { ltres: 0.01, qtres: 0.01, pathomit: 8, rightangleenhance: false };
          } else if (preset === 'sharp') {
            options = { qtres: 0.01, ltres: 0.01, pathomit: 8, rightangleenhance: true, linefilter: true };
          } else if (preset === 'detailed') {
            options = { pathomit: 0, roundcoords: 2, ltres: 0.5, qtres: 0.5, numberofcolors: colorCount[0] };
          } else if (preset === 'grayscale') {
            options = { colorsampling: 0, colorquantcycles: 1, numberofcolors: 8 };
          } else {
            options = { numberofcolors: colorCount[0] };
          }
          
          const svgString = ImageTracer.imagedataToSVG(imageData, options);
          resolve({ svgString, width: img.width, height: img.height });
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageUrl;
    });
  };

  const processImages = async (files) => {
    setIsProcessing(true);
    setProgress(0);
    const newImages = [];

    for (let i = 0; i < files.length; i++) {
      try {
        setProgress(Math.round((i / files.length) * 100));
        const file = files[i];
        const imageUrl = URL.createObjectURL(file);
        const { svgString, width, height } = await traceImage(imageUrl);
        
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml' });
        const svgFile = new File([svgBlob], file.name.replace(/\.(jpg|jpeg)$/i, '.svg'), { type: 'image/svg+xml' });
        
        newImages.push({
          id: Date.now() + i,
          name: file.name,
          originalSize: file.size,
          originalPreview: imageUrl,
          dimensions: { width, height },
          svgFile,
          svgSize: svgBlob.size,
          svgPreview: URL.createObjectURL(svgBlob),
        });
      } catch (error) {
        console.error(`Error converting ${files[i].name}:`, error);
        toast.error(`Failed to convert ${files[i].name}`);
      }
    }

    setProgress(100);
    setImages(prev => [...prev, ...newImages]);
    setIsProcessing(false);
    if (newImages.length > 0) toast.success(`Converted ${newImages.length} image(s) to SVG!`);
  };

  const downloadImage = (image) => saveAs(image.svgFile, image.svgFile.name);

  const downloadAllAsZip = async () => {
    const zip = new JSZip();
    for (const img of images) {
      zip.file(img.svgFile.name, img.svgFile);
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, "jpg-to-svg-converted.zip");
  };

  const formatSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-orange-50 to-amber-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-slate-800 dark:text-white">JPG to SVG</h1>
              <p className="text-xs text-slate-500 dark:text-slate-400">by KayTool</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <QuickConvert />
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 border-orange-200 text-orange-600 bg-orange-50">
              <RefreshCw className="w-3 h-3" /> JPG → SVG
            </Badge>
            <Button variant="ghost" size="icon" onClick={() => setDarkMode(prev => !prev)}>
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-white mb-3">Convert JPG to SVG</h2>
          <p className="text-slate-600 dark:text-slate-300">Vectorize JPG images to scalable SVG format. Free and private.</p>
        </div>

        <div className="flex flex-wrap justify-center gap-4 mb-6">
          <Card className="p-4 bg-white dark:bg-slate-800 w-full max-w-xs">
            <div className="flex items-center gap-2 mb-2">
              <Palette className="w-4 h-4 text-orange-600" />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Preset</span>
            </div>
            <Select value={preset} onValueChange={setPreset}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(presets).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Card>
          <Card className="p-4 bg-white dark:bg-slate-800 w-full max-w-xs">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Colors</span>
              <span className="text-sm font-bold text-orange-600">{colorCount[0]}</span>
            </div>
            <Slider value={colorCount} onValueChange={setColorCount} min={2} max={64} step={2} className="w-full" />
          </Card>
        </div>

        <div
          className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
            ${isDragging ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20' : 'border-slate-300 dark:border-slate-600 hover:border-orange-400 bg-white dark:bg-slate-800'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,image/jpeg" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
          <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-orange-600' : 'text-slate-400'}`} />
          <p className="text-lg font-medium text-slate-700 dark:text-slate-200">Drop JPG files here or click to upload</p>
          <p className="text-sm text-slate-500 mt-1">Multiple files supported</p>
        </div>

        {isProcessing && (
          <div className="mt-6">
            <Progress value={progress} className="h-2" />
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-2 text-center">Vectorizing... {progress}%</p>
          </div>
        )}

        {images.length > 0 && (
          <div className="mt-8 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">Converted Images ({images.length})</h3>
              <div className="flex gap-2">
                <input ref={addMoreInputRef} type="file" accept=".jpg,.jpeg,image/jpeg" multiple onChange={(e) => { processImages(Array.from(e.target.files)); e.target.value = ''; }} className="hidden" />
                <Button variant="outline" size="sm" onClick={() => addMoreInputRef.current?.click()} className="text-green-600 border-green-200">
                  <Plus className="w-4 h-4 mr-1" /> Add More
                </Button>
                <Button variant="outline" size="sm" onClick={() => setImages([])} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                {images.length > 1 && (
                  <Button size="sm" onClick={downloadAllAsZip} className="bg-orange-600 hover:bg-orange-700">
                    <Download className="w-4 h-4 mr-1" /> Download All (ZIP)
                  </Button>
                )}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {images.map((image) => (
                <Card key={image.id} className="overflow-hidden bg-white dark:bg-slate-800">
                  <div className="grid grid-cols-2 gap-2 p-2">
                    <div className="aspect-video relative bg-slate-100 dark:bg-slate-700 rounded">
                      <img src={image.originalPreview} alt="Original" className="w-full h-full object-contain" />
                      <Badge className="absolute bottom-1 left-1 text-xs bg-slate-600">Original</Badge>
                    </div>
                    <div className="aspect-video relative bg-white dark:bg-slate-600 rounded border">
                      <img src={image.svgPreview} alt="SVG" className="w-full h-full object-contain" />
                      <Badge className="absolute bottom-1 left-1 text-xs bg-orange-600">SVG</Badge>
                    </div>
                  </div>
                  <button onClick={() => setImages(prev => prev.filter(i => i.id !== image.id))} className="absolute top-2 right-2 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600">
                    <X className="w-4 h-4" />
                  </button>
                  <CardContent className="p-4 pt-0">
                    <p className="font-medium text-slate-800 dark:text-white truncate text-sm">{image.name}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <span>{formatSize(image.originalSize)}</span>
                      <span>→</span>
                      <span className="font-medium text-orange-600">{formatSize(image.svgSize)}</span>
                      <span className="ml-auto">{image.dimensions.width}×{image.dimensions.height}</span>
                    </div>
                    <Button onClick={() => downloadImage(image)} className="w-full mt-3 bg-orange-600 hover:bg-orange-700" size="sm">
                      <Download className="w-4 h-4 mr-1" /> Download SVG
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center text-sm text-slate-500">
          <p>© 2025 KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>
    </div>
  );
}
